import { useCallback, useMemo, useState } from "react";
import { initialRequisicaoServicosProdutosListaFilters, useRequisicaoServicosProdutosListaFilters } from "../hooks/RequisicaoServicosProdutos_lista/filters";
import { useRequisicaoServicosProdutosListaPagination } from "../hooks/RequisicaoServicosProdutos_lista/pagination";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { ProdutoServicoDropDown } from "../../../../common/domain/models/produto_servico_dropdown";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { RequisicaoCompraLista } from "../../domain/models/requisicao_compra_lista";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { EmailForm } from "../../../../common/presentation/schemas/email";
import { TelasIds } from "../../../../common/utils/tela_ids";

/**
 * LoadRequisicoesFuncState
 */

type LoadRequisicoesFuncState = Partial<{
  nroRequisicao: number | null;
  dataRequisicaoStart: Date | null;
  dataRequisicaoEnd: Date | null;
  centroCustos: number[] | null;
  solicitantes: number[] | null;
  status: number | null;
  produtosServicos: number[] | null;
  dataPrevistaStart: Date | null;
  dataPrevistaEnd: Date | null;
  page: number;
  rowsPerPage: number;
}>;

/**
 * useRequisicaoServicosProdutosListaViewModel
 */

export const useRequisicaoServicosProdutosListaViewModel = () => {
  const {
    compras: {
      getAllRequisicaoCompraUseCase,
      deleteRequisicaoCompraUseCase,
      sendEmailRequisicaoCompraUseCase,
      copyRequisicaoCompraUseCase,
      finalizarLoteRequisicaoCompraUseCase,
    },
    reports: {
      getReportRequisicaoCompraUseCase,
    },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadProdutoServicosDropDown,
  } = useDropDownLoader();

  const {
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    setPageSaving,
  } = usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [requisicoes, setRequisicoes] = useState<RequisicaoCompraLista[]>();
  const [selectedRequisicao, setSelectedRequisicao] = useState<RequisicaoCompraLista>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [funcionarioDropdown, setFuncionarioDropdown] = useState<FuncionarioDropDown[]>();
  const [produtosServicosDropdown, setProdutosServicosDropdown] = useState<ProdutoServicoDropDown[]>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

  const [requisicaoProdutoServicoDeleteModal, setRequisicaoProdutoServicoDeleteModal] = useState(false);
  const [emailModal, setEmailModal] = useState(false);

  const [emailSendTo, setEmailSendTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailAttachment, setEmailAttachment] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useRequisicaoServicosProdutosListaFilters();
  const pagination = useRequisicaoServicosProdutosListaPagination();

  const filteredFuncionarioDropdown = useMemo(() => {
    if (funcionarioDropdown === undefined) return null;
    return funcionarioDropdown.filter(x => x.canBeSelected);
  }, [
    funcionarioDropdown,
  ]);

  /**
   * Funções de carregamento
   */

  const loadRequisicoes = useCallback(async (fs?: LoadRequisicoesFuncState) => {
    try {
      const filteredNroRequisicao = evaluateParamValue(fs?.nroRequisicao, filters.nroRequisicao);
      const filteredDataRequisicaoStart = evaluateParamValue(fs?.dataRequisicaoStart, filters.dataRequisicaoStart);
      const filteredDataRequisicaoEnd = evaluateParamValue(fs?.dataRequisicaoEnd, filters.dataRequisicaoEnd);
      const filteredCentroCustos = evaluateParamValue(fs?.centroCustos, filters.centroCustos);
      const filteredSolicitantes = evaluateParamValue(fs?.solicitantes, filters.solicitantes);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredProdutosServicos = evaluateParamValue(fs?.produtosServicos, filters.produtosServicos);
      const filteredDataPrevistaStart = evaluateParamValue(fs?.dataPrevistaStart, filters.dataPrevistaStart);
      const filteredDataPrevistaEnd = evaluateParamValue(fs?.dataPrevistaEnd, filters.dataPrevistaEnd);
      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllRequisicaoCompraUseCase.getAllRequisicaoCompra(
        filteredNroRequisicao,
        filteredDataRequisicaoStart,
        filteredDataRequisicaoEnd,
        filteredCentroCustos,
        filteredSolicitantes,
        filteredStatus,
        filteredProdutosServicos,
        filteredDataPrevistaStart,
        filteredDataPrevistaEnd,
        filteredPage,
        filteredRowsPerPage,
      );

      setRequisicoes(getAllRequisicaoCompraUseCase.items);
      pagination.setTotalItems(getAllRequisicaoCompraUseCase.totalItems);
    } catch (e: unknown) {
      setRequisicoes(undefined);
      throw new Error('Falha ao buscar dados da página!');
    }
  }, [
    filters,
    pagination,
    getAllRequisicaoCompraUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionarioDropdown(dt), { filtrarCentroCusto: false });
      await loadProdutoServicosDropDown(dt => setProdutosServicosDropdown(dt), {});
      await loadRequisicoes();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionarioDropdown(dt), { filtrarCentroCusto: false });
      await loadProdutoServicosDropDown(dt => setProdutosServicosDropdown(dt), {});
      await loadRequisicoes();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadProdutoServicosDropDown,
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleRequisicaoProdutoServicoDeleteSubmit = useCallback(async () => {
    if (selectedRequisicao) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteRequisicaoCompraUseCase
          .deleteRequisicaoCompra(TelasIds.RequisicaoServicosProdutos_lista, selectedRequisicao.idRequisicaoCompra);

        await loadRequisicoes();

        setRequisicaoProdutoServicoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!')[0]);
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedRequisicao,
    deleteRequisicaoCompraUseCase,
    loadRequisicoes,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleRequisicaoProdutoServicoExportar = useCallback(async (idRequisicaoCompra: number) => {
    try {
      setLoading(true);
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportRequisicaoCompraUseCase.getReportRequisicaoCompra(
            idRequisicaoCompra, requiredMimeType),
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatório!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportRequisicaoCompraUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleEmailSubmit = useCallback(async (form: EmailForm) => {
    if (selectedRequisicao) {
      try {
        setLoading(true);

        await sendEmailRequisicaoCompraUseCase
          .sendEmailRequisicaoCompra(
            selectedRequisicao.idRequisicaoCompra,
            form.subject,
            form.sendTo,
            form.body,
            form.attachment,
          );

        pushInfoNotification("Sucesso", "Email enviado com sucesso!");
        setEmailModal(false);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao enviar email!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    selectedRequisicao,
    sendEmailRequisicaoCompraUseCase,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleCopyRequisicaoProdutoServicoSubmit = useCallback(async (idRequisicaoCompra: number) => {
    try {
      setSaving(true);
      setPageSaving(true);

      await copyRequisicaoCompraUseCase.copyRequisicaoCompra(
        idRequisicaoCompra,
      );

      await loadRequisicoes();
      pushInfoNotification("Sucesso", "Requisição copiada com sucesso!");
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    copyRequisicaoCompraUseCase,
    loadRequisicoes,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleFinalizarLoteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (rowSelectionModel && rowSelectionModel.ids.size > 0) {
        await finalizarLoteRequisicaoCompraUseCase
          .finalizarLoteRequisicaoCompra(rowSelectionModel.ids);

        await loadRequisicoes();

        pushInfoNotification("Sucesso", "Requisições finalizadas com sucesso!");

        setRowSelectionModel({ type: 'include', ids: new Set() });
      } else {
        pushWarnNotification('Alerta', "Você deve selecionar pelo menos 1 requisição para ser finalizada!");
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    rowSelectionModel,
    finalizarLoteRequisicaoCompraUseCase,
    loadRequisicoes,
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosListaPagination.setState({ page: page + 1 });
      await loadRequisicoes({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosListaPagination.setState({ page: 1, rowsPerPage });
      await loadRequisicoes({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleRowSelectionModel = useCallback((model: GridRowSelectionModel) => {
    setRowSelectionModel(model);
  }, [
  ]);

  const handleNroRequisicaoChange = useDebouncedCallback(debouncedHandler, async (nroRequisicao: number | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ nroRequisicao });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ nroRequisicao, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleDataRequisicaoChange = useDebouncedCallback(debouncedHandler, (dataRequisicaoStart: Date | null, dataRequisicaoEnd: Date | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ dataRequisicaoStart, dataRequisicaoEnd });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ dataRequisicaoStart, dataRequisicaoEnd, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleCentroCustosChange = useDebouncedCallback(debouncedHandler, (centroCustos: number[] | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ centroCustos });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ centroCustos, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleSolicitantesChange = useDebouncedCallback(debouncedHandler, (solicitantes: number[] | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ solicitantes });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ solicitantes, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, (status: number | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleProdutosServicosChange = useDebouncedCallback(debouncedHandler, (produtosServicos: number[] | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ produtosServicos });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ produtosServicos, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleDataPrevistaChange = useDebouncedCallback(debouncedHandler, (dataPrevistaStart: Date | null, dataPrevistaEnd: Date | null) => {
    useRequisicaoServicosProdutosListaFilters.setState({ dataPrevistaStart, dataPrevistaEnd });
    return async () => {
      try {
        setLoading(true);
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        await loadRequisicoes({ dataPrevistaStart, dataPrevistaEnd, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleClearFilter = useCallback(async () => {
    try {
      setLoading(true);
      useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters });
      useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
      await loadRequisicoes({ ...initialRequisicaoServicosProdutosListaFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadRequisicoes,
    pushErrorNotification,
  ]);

  const handleRequisicaoProdutoServicoModalOpenState = useCallback((idRequisicaoCompra: number | null, openNewTab: boolean) => {
    if (idRequisicaoCompra) {
      if (openNewTab) {
        window.open(`/RequisicaoServicosProdutos_cadastro?id=${idRequisicaoCompra}`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/RequisicaoServicosProdutos_cadastro?id=${idRequisicaoCompra}`, { alternativeRoute: `/RequisicaoServicosProdutos_lista` });
      }
    } else {
      if (openNewTab) {
        window.open(`/RequisicaoServicosProdutos_cadastro`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/RequisicaoServicosProdutos_cadastro`, { alternativeRoute: `/RequisicaoServicosProdutos_lista` });
      }
    }
  }, [
    backOrNavigate,
  ]);

  const handleRequisicaoProdutoServicoDeleteModalOpenState = useCallback((idRequisicaoCompra: number | null, openState: boolean) => {
    if (openState) {
      if (idRequisicaoCompra && requisicoes) {
        setSelectedRequisicao(requisicoes.find(x => idRequisicaoCompra === x.idRequisicaoCompra));
      } else {
        setSelectedRequisicao(undefined);
      }
    }
    setRequisicaoProdutoServicoDeleteModal(openState);
  }, [
    requisicoes,
  ]);

  const handleEmailOpenState = useCallback((idRequisicaoCompra: number | null, openState: boolean) => {
    if (openState) {
      if (idRequisicaoCompra && requisicoes) {
        const requisicao = requisicoes.find(x => x.idRequisicaoCompra === idRequisicaoCompra);
        if (requisicao) {
          setEmailSendTo("");
          setEmailSubject("Requisição de Compra - " + requisicao.nrRequisicaoCompra);
          setEmailAttachment(`REQUISICAO_COMPRA_${requisicao.nrRequisicaoCompra}.pdf`);
          setEmailBody(emailTemplates.requisicaoCompra);
        }
        setSelectedRequisicao(requisicao);
      } else {
        setSelectedRequisicao(undefined);
      }
    }
    setEmailModal(openState);
  }, [
    requisicoes,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    requisicoes: requisicoes ?? [],
    selectedRequisicao: selectedRequisicao ?? null,
    centroCustoDropdown: centroCustoDropdown ?? [],
    funcionarioDropdown: filteredFuncionarioDropdown ?? [],
    produtosServicosDropdown: produtosServicosDropdown ?? [],
    rowSelectionModel,
    requisicaoProdutoServicoDeleteModal,
    emailModal,
    emailSendTo,
    emailSubject,
    emailAttachment,
    emailBody,
    reportViewerState,
    filters,
    pagination,
    handleReload,
    handleRequisicaoProdutoServicoDeleteSubmit,
    handleRequisicaoProdutoServicoExportar,
    handleEmailSubmit,
    handleCopyRequisicaoProdutoServicoSubmit,
    handleFinalizarLoteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowSelectionModel,
    handleNroRequisicaoChange,
    handleDataRequisicaoChange,
    handleCentroCustosChange,
    handleSolicitantesChange,
    handleStatusChange,
    handleProdutosServicosChange,
    handleDataPrevistaChange,
    handleClearFilter,
    handleRequisicaoProdutoServicoModalOpenState,
    handleRequisicaoProdutoServicoDeleteModalOpenState,
    handleEmailOpenState,
  };
}